/**
 * ID generation helpers (canonical for API + web).
 * Prefer `generateId` / `generateSimpleId` / `makePrefixedId` from here instead of duplicating under each app `src/utils/`.
 */

import type { Property } from '@repo/shared-types';

/**
 * Prefixes passed to `makePrefixedId` for entities whose DB PK is native UUID (accounting core).
 * For these, return a bare RFC-4122 UUID; otherwise keep `prefix_timestamp_uuid` (wax / TEXT PKs).
 */
export const STEEL_ACCOUNTING_ID_PREFIXES: ReadonlySet<string> = new Set([
  'bill',
  'rec',
  'rec_pay',
  'receivable',
]);

function randomUuidWebCrypto(): string {
  const c = globalThis.crypto;
  if (c?.randomUUID) {
    return c.randomUUID();
  }
  const buf = new Uint8Array(16);
  if (c?.getRandomValues) {
    c.getRandomValues(buf);
    return Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  throw new Error('makePrefixedId requires Web Crypto (randomUUID or getRandomValues)');
}

/**
 * Opaque ID: for accounting-core steel prefixes, returns `randomUUID()` only; else `prefix_${Date.now()}_${uuid}`.
 */
export function makePrefixedId(prefix: string): string {
  const key = prefix.trim().toLowerCase();
  if (STEEL_ACCOUNTING_ID_PREFIXES.has(key)) {
    return randomUuidWebCrypto();
  }
  const timestamp = Date.now();
  const c = globalThis.crypto;
  if (c?.randomUUID) {
    return `${prefix}_${timestamp}_${c.randomUUID()}`;
  }
  const buf = new Uint8Array(16);
  if (c?.getRandomValues) {
    c.getRandomValues(buf);
    const hex = Array.from(buf, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${prefix}_${timestamp}_${hex}`;
  }
  throw new Error('makePrefixedId requires Web Crypto (randomUUID or getRandomValues)');
}

export function getUnitId(propertyId: string, propertyType: string, unitNumber?: string): string {
  if (propertyType === 'Single Family') {
    return propertyId;
  }
  return `unit_${propertyId}_${unitNumber || '1'}`;
}

export function isValidUnitId(unitId: string): boolean {
  if (!unitId || unitId.trim() === '') {
    return false;
  }
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(unitId) || /^unit_[a-zA-Z0-9_-]+$/.test(unitId);
}

export function getUnitDesignation(property: Property, unitIndex: number = 0): string {
  switch (property.type) {
    case 'Single Family':
      return '';
    case 'Duplex':
      return ['A', 'B'][unitIndex] || String(unitIndex + 1);
    case 'Multifamily':
    case 'Commercial':
    default:
      return String(unitIndex + 1).padStart(3, '0');
  }
}

export function getUnitDisplayString(unitId: string, propertyType: string, showDashForSingleFamily: boolean = false): string {
  if (propertyType === 'Single Family') {
    return showDashForSingleFamily ? '-' : '';
  }

  const parts = unitId.split('_');
  if (parts.length < 3) {
    return showDashForSingleFamily ? '—' : '—';
  }

  const unitNumber = parts[2];

  if (propertyType === 'Duplex') {
    const num = Number.parseInt(unitNumber, 10);
    if (num === 1) return 'A';
    if (num === 2) return 'B';
    return unitNumber;
  }

  const num = Number.parseInt(unitNumber, 10);
  if (!isNaN(num)) {
    return String(num).padStart(3, '0');
  }

  return unitNumber;
}

export function generateId(prefix: string = 'id'): string {
  const timestamp = Date.now();
  const c = globalThis.crypto;
  if (c?.randomUUID) {
    return `${prefix}_${timestamp}_${c.randomUUID()}`;
  }
  const buf = new Uint8Array(16);
  if (c?.getRandomValues) {
    c.getRandomValues(buf);
    const hex = Array.from(buf, b => b.toString(16).padStart(2, '0')).join('');
    return `${prefix}_${timestamp}_${hex}`;
  }
  throw new Error('generateId requires Web Crypto (randomUUID or getRandomValues)');
}

export function generateSimpleId(prefix: string = 'id'): string {
  return `${prefix}_${Date.now()}`;
}
